// src/admin/components/integrations/TicketingSettingsFields.jsx
import React from 'react';
import PropTypes from 'prop-types';
import LabelPill from '../../../shared/components/pill/LabelPill';
import useTicketingOptions from '../../hooks/useTicketingOptions';

/** One lifecycle moment: comment, transition, neither, or both. */
function TicketActionField({ field, value, transitions, onChange }) {
  const action = { comment: true, transition_id: null, ...(field.default || {}), ...(value || {}) };

  return (
    <fieldset className="border rounded p-2 mb-3">
      <legend className="fs-6 text-muted px-1 mb-2" style={{ fontSize: '0.85rem', width: 'auto' }}>
        {field.label}
      </legend>

      <div className="form-check form-switch mb-2">
        <input
          className="form-check-input"
          type="checkbox"
          id={`action-comment-${field.key}`}
          checked={Boolean(action.comment)}
          onChange={(e) => onChange(field.key, { ...action, comment: e.target.checked })}
        />
        <label className="form-check-label" htmlFor={`action-comment-${field.key}`}>
          Post a comment
        </label>
      </div>

      <label className="form-label small text-muted" htmlFor={`action-transition-${field.key}`}>
        Move the ticket to
      </label>
      <select
        className="form-select form-select-sm"
        id={`action-transition-${field.key}`}
        value={action.transition_id || ''}
        onChange={(e) => onChange(field.key, { ...action, transition_id: e.target.value || null })}
      >
        <option value="">Leave the status unchanged</option>
        {transitions.map((t) => (
          <option key={t.id} value={t.id}>{t.name}</option>
        ))}
      </select>
    </fieldset>
  );
}

TicketActionField.propTypes = {
  field: PropTypes.shape({
    key: PropTypes.string, label: PropTypes.string, default: PropTypes.object,
  }).isRequired,
  value: PropTypes.object,
  transitions: PropTypes.arrayOf(PropTypes.object).isRequired,
  onChange: PropTypes.func.isRequired,
};
TicketActionField.defaultProps = { value: null };

/**
 * Renders an integration's `settings_schema`. Choices for fields carrying `options_source` are
 * fetched from the live vendor instance, since Jira projects and transitions are per-site.
 */
export default function TicketingSettingsFields({ detail, values, onChange }) {
  const schema = detail?.settings_schema || [];
  const { projects, transitions, loading, error } = useTicketingOptions(detail?.id, {
    enabled: schema.length > 0,
  });

  if (schema.length === 0) return null;

  const toggleProject = (key, projectKey, selected) => {
    const current = values[key] || [];
    onChange(key, selected ? [...current, projectKey] : current.filter((k) => k !== projectKey));
  };

  return (
    <div>
      <h6 className="text-muted fw-normal mb-2">Ticketing</h6>

      {error && <div className="alert alert-warning py-2 small" role="alert">{error}</div>}
      {loading && <div className="text-muted small mb-2">Loading options…</div>}

      {schema.map((field) => {
        if (field.type === 'multiselect') {
          const selected = values[field.key] || [];
          return (
            <div key={field.key} className="mb-3">
              <span className="form-label d-block">{field.label}</span>
              {projects.length === 0 && !loading && (
                <div className="text-muted small">No projects available.</div>
              )}
              {projects.map((p) => (
                <div className="form-check" key={p.key}>
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id={`setting-${field.key}-${p.key}`}
                    checked={selected.includes(p.key)}
                    onChange={(e) => toggleProject(field.key, p.key, e.target.checked)}
                  />
                  <label className="form-check-label" htmlFor={`setting-${field.key}-${p.key}`}>
                    {`${p.key} — ${p.name}`}
                    {p.project_type === 'service_desk' && (
                      <LabelPill tone="info" size="inline" className="ms-2">Service desk</LabelPill>
                    )}
                  </label>
                </div>
              ))}
              {field.help && <div className="form-text">{field.help}</div>}
            </div>
          );
        }

        if (field.type === 'boolean') {
          return (
            <div key={field.key} className="form-check form-switch mb-3">
              <input
                className="form-check-input"
                type="checkbox"
                id={`setting-${field.key}`}
                checked={values[field.key] ?? field.default ?? false}
                onChange={(e) => onChange(field.key, e.target.checked)}
              />
              <label className="form-check-label" htmlFor={`setting-${field.key}`}>
                {field.label}
              </label>
              {field.help && <div className="form-text">{field.help}</div>}
            </div>
          );
        }

        if (field.type === 'ticket_action') {
          return (
            <TicketActionField
              key={field.key}
              field={field}
              value={values[field.key]}
              transitions={transitions}
              onChange={onChange}
            />
          );
        }

        return null;
      })}
    </div>
  );
}

TicketingSettingsFields.propTypes = {
  detail: PropTypes.shape({ id: PropTypes.number, settings_schema: PropTypes.array }),
  values: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
};
TicketingSettingsFields.defaultProps = { detail: null };
